import { ChangeEvent, useRef, useState } from 'react'

import { Icon, Text, TextField, Tooltip } from '@joor/design-system'

import { BillingContact } from '__generated__/atlas-types'

import { ModalIds } from '../../billingContactModal.ids'
import messages from '../../billingContactModal.messages'
import useStore from '../../billingContactModal.store'
import { isEmailRepeated, isValidEmail } from '../../billingContactModal.utils'
import styles from './BillingContactForm.module.css'

interface BillingContactFormProps {
  index: number
  contacts: BillingContact[]
}

const BillingContactForm = ({ index, contacts }: BillingContactFormProps) => {
  const validateTimeout = useRef<NodeJS.Timeout | null>(null)
  const isLoading = useStore((state) => state.isLoading)
  const setBillingContactAtIndex = useStore(
    (state) => state.setBillingContactAtIndex,
  )
  const removeBillingContactAtIndex = useStore(
    (state) => state.removeBillingContactAtIndex,
  )
  const [emailErrorMsg, setEmailErrorMsg] = useState('')

  const contact = contacts[index]

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value.trim()
    setBillingContactAtIndex(index, { ...contact, email })
    if (validateTimeout.current) {
      clearTimeout(validateTimeout.current)
    }
    validateTimeout.current = setTimeout(() => {
      if (!isValidEmail(email)) {
        return setEmailErrorMsg(messages.invalidEmail)
      } else if (isEmailRepeated(contacts, email, index)) {
        return setEmailErrorMsg(messages.emailInUse)
      }
      setEmailErrorMsg('')
    }, 500)
  }

  return (
    <div
      className={styles.Container}
      id={ModalIds.ContactForm(index)}
      data-testid={ModalIds.ContactForm(index)}
    >
      <div className={styles.HeaderContainer}>
        <Text
          id={ModalIds.ContactFormTitle(index)}
          data-testid={ModalIds.ContactFormTitle(index)}
          letterSpacing="tighter"
          fontWeight="bold"
          color="primary-700"
        >
          {messages.contactN(index + 1)}
        </Text>
        <Tooltip
          variant="error"
          customContentStyles={{ maxWidth: '208px' }}
          content={
            isLoading
              ? undefined
              : contacts.length <= 1
                ? messages.atLeastOneRequired
                : undefined
          }
        >
          <Icon
            id={ModalIds.RemoveContactButton(index)}
            data-testid={ModalIds.RemoveContactButton(index)}
            className={styles.RemoveIcon}
            iconName="trash"
            iconSize="small"
            variant={!isLoading && contacts.length > 1 ? 'DANGER' : 'DISABLED'}
            onClick={() => {
              if (!isLoading && contacts.length > 1) {
                removeBillingContactAtIndex(index)
              }
            }}
          />
        </Tooltip>
      </div>
      <TextField
        id={ModalIds.NameInput(index)}
        data-testid={ModalIds.NameInput(index)}
        className={styles.TextField}
        disabled={isLoading}
        label={messages.name}
        placeholder={messages.contactName}
        value={contact.name}
        isRequired
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setBillingContactAtIndex(index, {
            ...contact,
            name: e.target.value,
          })
        }}
      />
      <TextField
        id={ModalIds.EmailInput(index)}
        data-testid={ModalIds.EmailInput(index)}
        className={styles.TextField}
        disabled={isLoading}
        label={messages.email}
        placeholder={messages.emailAddress}
        value={contact.email}
        isRequired
        onChange={handleEmailChange}
        errorMessage={emailErrorMsg ? emailErrorMsg : ''}
      />
    </div>
  )
}

export default BillingContactForm
